import { useEffect, useState } from "react";
import souqMadarMark from "@/assets/brand/souq-madar-mark.png";
import {
    Link,
    useParams,
} from "react-router-dom";
import { toast } from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";
import "./InvoicePrint.css";
import {
    Button,
} from "@/components/ui/button";


export default function Invoice() {
    const {
        orderId,
        sellerId,
    } = useParams();

    const {
        lang,
        t,
    } = useLang();

    const [invoice, setInvoice] =
        useState(null);

    const [loading, setLoading] =
        useState(true);


    const tr = (
        ar,
        en
    ) => (
        lang === "ar"
            ? ar
            : en
    );


    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const response =
                    await api.get(
                        `/orders/${orderId}/invoices/${sellerId}`
                    );

                if (active) {
                    setInvoice(
                        response.data
                    );
                }

            } catch (error) {
                toast.error(
                    formatApiError(
                        error.response
                            ?.data
                            ?.detail
                    )
                    || error.message
                );

            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            active = false;
        };

    }, [
        orderId,
        sellerId,
    ]);


    const money = (value) => (
        Number(
            value || 0
        ).toFixed(2)
    );


    const dateTime = (value) => {
        if (!value) {
            return "-";
        }

        const date = new Date(
            value
        );

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleString(
            lang === "ar"
                ? "ar-SA"
                : "en-US"
        );
    };


    if (loading) {
        return (
            <Layout>
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-20 text-center text-muted-foreground">
                    {tr(
                        "\u062c\u0627\u0631\u064a \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629...",
                        "Loading invoice..."
                    )}
                </div>
            </Layout>
        );
    }


    if (!invoice) {
        return (
            <Layout>
                <div className="max-w-xl mx-auto px-4 py-20 text-center">
                    <p className="text-muted-foreground">
                        {tr(
                            "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
                            "Could not load invoice"
                        )}
                    </p>

                    <Button
                        asChild
                        variant="outline"
                        className="mt-6 rounded-full"
                    >
                        <Link to="/orders">
                            {tr(
                                "\u0627\u0644\u0639\u0648\u062f\u0629 \u0625\u0644\u0649 \u0637\u0644\u0628\u0627\u062a\u064a",
                                "Back to my orders"
                            )}
                        </Link>
                    </Button>
                </div>
            </Layout>
        );
    }


    const official = Boolean(
        invoice.official
    );

    const issuer = (
        invoice.issuer_snapshot
        || {}
    );

    const customer = (
        invoice.customer_snapshot
        || {}
    );

    const tax = (
        invoice.tax_snapshot
        || {}
    );

    const totals = (
        invoice.totals_snapshot
        || {}
    );

    const payment = (
        invoice.payment_snapshot
        || {}
    );

    const items = (
        invoice.items
        || []
    );

    const productsTotal = Number(
        totals.products_after_discount
        || 0
    );

    const shippingTotal = Number(
        totals.order_shipping_total
        || 0
    );

    const grandTotal = (
        productsTotal
        + shippingTotal
    );


    return (
        <Layout>
            <div className="invoice-print-page max-w-6xl mx-auto px-4 md:px-8 py-10">

                <div className="rounded-[28px] border border-border bg-white shadow-sm overflow-hidden">
                    <div className="p-6 md:p-8">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">

                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl overflow-hidden shrink-0">
                                    <img
                                        src={souqMadarMark}
                                        alt={t("appName")}
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                <div>
                                    <div className="text-2xl md:text-3xl font-black text-espresso leading-none">
                                        {t("appName")}
                                    </div>

                                    <div className="mt-2 text-sm text-muted-foreground">
                                        {t("tagline")}
                                    </div>

                                    <div className="mt-1 text-xs font-bold text-espresso/70">
                                        {issuer.legal_name || ""}
                                    </div>
                                </div>
                            </div>


                            <div className="lg:text-end">
                                <div className="flex flex-wrap lg:justify-end items-center gap-2">
                                    <span
                                        className={
                                            official
                                                ? "inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-black text-green-800"
                                                : "inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-800"
                                        }
                                    >
                                        {official
                                            ? tr(
                                                "\u0635\u0627\u062f\u0631\u0629",
                                                "Issued"
                                            )
                                            : tr(
                                                "\u0642\u064a\u062f \u0627\u0644\u062a\u062c\u0647\u064a\u0632",
                                                "Being prepared"
                                            )}
                                    </span>
                                </div>

                                <h1 className="mt-3 text-3xl md:text-4xl font-black text-espresso">
                                    {official
                                        ? tr(
                                            "\u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629 \u0627\u0644\u0636\u0631\u064a\u0628\u064a\u0629",
                                            "Tax Invoice"
                                        )
                                        : tr(
                                            "\u0645\u0639\u0627\u064a\u0646\u0629 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
                                            "Invoice Preview"
                                        )}
                                </h1>

                                <p className="mt-2 text-sm text-muted-foreground">
                                    {official
                                        ? tr(
                                            "\u0641\u0627\u062a\u0648\u0631\u0629 \u0635\u0627\u062f\u0631\u0629 \u0631\u0633\u0645\u064a\u064b\u0627",
                                            "Officially issued invoice"
                                        )
                                        : tr(
                                            "\u0647\u0630\u0647 \u0645\u0639\u0627\u064a\u0646\u0629 \u063a\u064a\u0631 \u0635\u0627\u062f\u0631\u0629 \u0631\u0633\u0645\u064a\u064b\u0627 \u0628\u0639\u062f.",
                                            "This is a preview and has not been officially issued yet."
                                        )}
                                </p>
                            </div>
                        </div>


                        <div className="mt-7 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">

                                <div>
                                    <span className="text-muted-foreground">
                                        {tr(
                                            "\u0631\u0642\u0645 \u0627\u0644\u0637\u0644\u0628",
                                            "Order number"
                                        )}
                                    </span>

                                    <div className="mt-1 font-black text-espresso">
                                        {invoice.order_id}
                                    </div>
                                </div>

                                <div>
                                    <span className="text-muted-foreground">
                                        {tr(
                                            "\u0631\u0642\u0645 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
                                            "Invoice number"
                                        )}
                                    </span>

                                    <div className="mt-1 font-black text-espresso">
                                        {official
                                            ? invoice.invoice_number
                                            : tr(
                                                "\u063a\u064a\u0631 \u0645\u064f\u0635\u062f\u0631 \u0628\u0639\u062f",
                                                "Not issued yet"
                                            )}
                                    </div>
                                </div>
                            </div>

                            <div className="invoice-no-print flex flex-wrap items-center gap-3">
                                <Button
                                    type="button"
                                    onClick={() => window.print()}
                                    className="rounded-full"
                                >
                                    {tr(
                                        "\u0637\u0628\u0627\u0639\u0629 / \u062d\u0641\u0638 PDF",
                                        "Print / Save PDF"
                                    )}
                                </Button>

                                <Button
                                    asChild
                                    variant="outline"
                                    className="rounded-full"
                                >
                                    <Link to="/orders">
                                        {tr(
                                            "\u0627\u0644\u0639\u0648\u062f\u0629 \u0625\u0644\u0649 \u0637\u0644\u0628\u0627\u062a\u064a",
                                            "Back to my orders"
                                        )}
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>


                {!official && (
                    <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
                        {tr(
                            "\u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629 \u0642\u064a\u062f \u0627\u0644\u062a\u062c\u0647\u064a\u0632. \u0644\u0646 \u064a\u0638\u0647\u0631 \u0631\u0642\u0645 \u0641\u0627\u062a\u0648\u0631\u0629 \u0631\u0633\u0645\u064a \u062d\u062a\u0649 \u0627\u0643\u062a\u0645\u0627\u0644 \u0645\u062a\u0637\u0644\u0628\u0627\u062a \u0627\u0644\u0625\u0635\u062f\u0627\u0631.",
                            "Invoice preparation is in progress. An official invoice number will appear only after issuance requirements are complete."
                        )}
                    </div>
                )}


                <div className="mt-8 grid md:grid-cols-2 gap-6">

                    <section className="rounded-2xl border border-border bg-white p-6">
                        <h2 className="font-black text-lg text-espresso">
                            {tr(
                                "\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
                                "Invoice details"
                            )}
                        </h2>

                        <div className="mt-5 space-y-3 text-sm">

                            <Row
                                label={tr(
                                    "\u0631\u0642\u0645 \u0627\u0644\u0637\u0644\u0628",
                                    "Order number"
                                )}
                                value={
                                    invoice.order_id
                                }
                            />

                            <Row
                                label={tr(
                                    "\u0631\u0642\u0645 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
                                    "Invoice number"
                                )}
                                value={
                                    official
                                        ? invoice.invoice_number
                                        : tr(
                                            "\u063a\u064a\u0631 \u0645\u064f\u0635\u062f\u0631 \u0628\u0639\u062f",
                                            "Not issued yet"
                                        )
                                }
                            />

                            <Row
                                label={official
                                    ? tr(
                                        "\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0625\u0635\u062f\u0627\u0631",
                                        "Issue date"
                                    )
                                    : tr(
                                        "\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u062a\u062c\u0647\u064a\u0632",
                                        "Prepared date"
                                    )}
                                value={
                                    dateTime(
                                        official
                                            ? invoice.issued_at
                                            : invoice.prepared_at
                                    )
                                }
                            />

                            <Row
                                label={tr(
                                    "\u062d\u0627\u0644\u0629 \u0627\u0644\u062f\u0641\u0639",
                                    "Payment status"
                                )}
                                value={
                                    payment.payment_status
                                    === "paid"
                                        ? tr(
                                            "\u0645\u062f\u0641\u0648\u0639",
                                            "Paid"
                                        )
                                        : (
                                            payment.payment_status
                                            || "-"
                                        )
                                }
                            />
                        </div>
                    </section>


                    <section className="rounded-2xl border border-border bg-white p-6">
                        <h2 className="font-black text-lg text-espresso">
                            {tr(
                                "\u0645\u0635\u062f\u0631 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
                                "Invoice issuer"
                            )}
                        </h2>

                        <div className="mt-5 space-y-3 text-sm">
                            <Row
                                label={tr(
                                    "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a",
                                    "Legal name"
                                )}
                                value={
                                    issuer.legal_name
                                    || "-"
                                }
                            />

                            <Row
                                label={tr(
                                    "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062a\u062c\u0627\u0631\u064a",
                                    "Business name"
                                )}
                                value={
                                    issuer.business_name
                                    || "-"
                                }
                            />

                            <Row
                                label={tr(
                                    "\u0631\u0642\u0645 \u0627\u0644\u0633\u062c\u0644 / \u0627\u0644\u0648\u062b\u064a\u0642\u0629",
                                    "Business document number"
                                )}
                                value={
                                    issuer
                                        .business_document_number
                                    || "-"
                                }
                            />

                            <Row
                                label={tr(
                                    "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0636\u0631\u064a\u0628\u064a",
                                    "VAT number"
                                )}
                                value={
                                    issuer.vat_number
                                    || "-"
                                }
                            />
                        </div>
                    </section>
                </div>


                <section className="mt-6 rounded-2xl border border-border bg-white p-6">
                    <h2 className="font-black text-lg text-espresso">
                        {tr(
                            "\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0639\u0645\u064a\u0644",
                            "Customer details"
                        )}
                    </h2>

                    <div className="mt-5 grid sm:grid-cols-2 gap-x-10 gap-y-4 text-sm">
                        <Row
                            label={tr(
                                "\u0627\u0644\u0627\u0633\u0645",
                                "Name"
                            )}
                            value={
                                customer.name
                                || "-"
                            }
                        />

                        <Row
                            label={tr(
                                "\u0627\u0644\u0645\u062f\u064a\u0646\u0629",
                                "City"
                            )}
                            value={
                                customer.city
                                || "-"
                            }
                        />

                        <Row
                            label={tr(
                                "\u0627\u0644\u0647\u0627\u062a\u0641",
                                "Phone"
                            )}
                            value={
                                customer.phone
                                || "-"
                            }
                        />

                        <Row
                            label={tr(
                                "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
                                "Address"
                            )}
                            value={
                                customer.address
                                || "-"
                            }
                        />
                    </div>
                </section>


                <section className="mt-6 rounded-2xl border border-border bg-white overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="font-black text-lg text-espresso">
                            {tr(
                                "\u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a",
                                "Items"
                            )}
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-surface">
                                <tr>
                                    <th className="p-4 text-start">
                                        {tr(
                                            "\u0627\u0644\u0645\u0646\u062a\u062c",
                                            "Product"
                                        )}
                                    </th>

                                    <th className="p-4 text-center">
                                        {tr(
                                            "\u0627\u0644\u0643\u0645\u064a\u0629",
                                            "Qty"
                                        )}
                                    </th>

                                    <th className="p-4 text-center">
                                        {tr(
                                            "\u0633\u0639\u0631 \u0627\u0644\u0648\u062d\u062f\u0629",
                                            "Unit price"
                                        )}
                                    </th>

                                    <th className="p-4 text-center">
                                        {tr(
                                            "\u0627\u0644\u0636\u0631\u064a\u0628\u0629",
                                            "VAT"
                                        )}
                                    </th>

                                    <th className="p-4 text-end">
                                        {tr(
                                            "\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a",
                                            "Total"
                                        )}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {items.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                item.product_id
                                                || index
                                            }
                                            className="border-t border-border"
                                        >
                                            <td className="p-4 font-bold text-espresso">
                                                {item.title}
                                            </td>

                                            <td className="p-4 text-center">
                                                {item.quantity}
                                            </td>

                                            <td className="p-4 text-center">
                                                {money(
                                                    item.unit_price
                                                )}{" "}
                                                {tr(
                                                    "\u0631.\u0633",
                                                    "SAR"
                                                )}
                                            </td>

                                            <td className="p-4 text-center">
                                                {money(
                                                    item.supply_vat_amount
                                                    ?? item.vat_amount
                                                )}{" "}
                                                {tr(
                                                    "\u0631.\u0633",
                                                    "SAR"
                                                )}
                                            </td>

                                            <td className="p-4 text-end font-bold">
                                                {money(
                                                    item.gross_after_discount
                                                    ?? item.line_total
                                                )}{" "}
                                                {tr(
                                                    "\u0631.\u0633",
                                                    "SAR"
                                                )}
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>


                <section className="mt-6 rounded-2xl border border-border bg-white p-6 md:p-7">
                    <div>
                        <div>
                            <h2 className="font-black text-xl text-espresso">
                                {tr(
                                    "\u0627\u0644\u0645\u0644\u062e\u0635 \u0627\u0644\u0645\u0627\u0644\u064a",
                                    "Financial summary"
                                )}
                            </h2>

                            <p className="mt-1 text-xs text-muted-foreground">
                                {tr(
                                    "\u0627\u0644\u0623\u0633\u0639\u0627\u0631 \u0634\u0627\u0645\u0644\u0629 \u0636\u0631\u064a\u0628\u0629 \u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629",
                                    "Product prices include VAT"
                                )}
                            </p>
                        </div>


                    </div>


                    <div className="mt-6 max-w-xl me-auto space-y-4 text-sm">

                        <Row
                            label={tr(
                                "\u0627\u0644\u0645\u0628\u0644\u063a \u0642\u0628\u0644 \u0627\u0644\u0636\u0631\u064a\u0628\u0629",
                                "Amount before VAT"
                            )}
                            value={`${money(
                                tax.supply_taxable_net
                            )} ${tr(
                                "\u0631.\u0633",
                                "SAR"
                            )}`}
                        />

                        <Row
                            label={`${tr(
                                "\u0636\u0631\u064a\u0628\u0629 \u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629",
                                "VAT"
                            )} ${Number(
                                tax.supply_vat_rate
                                || 0
                            )}%`}
                            value={`${money(
                                tax.supply_vat_amount
                            )} ${tr(
                                "\u0631.\u0633",
                                "SAR"
                            )}`}
                        />

                        <Row
                            label={tr(
                                "\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a",
                                "Products total"
                            )}
                            value={`${money(
                                productsTotal
                            )} ${tr(
                                "\u0631.\u0633",
                                "SAR"
                            )}`}
                        />

                        <Row
                            label={tr(
                                "\u0631\u0633\u0648\u0645 \u0627\u0644\u0634\u062d\u0646",
                                "Shipping fees"
                            )}
                            value={`${money(
                                shippingTotal
                            )} ${tr(
                                "\u0631.\u0633",
                                "SAR"
                            )}`}
                        />

                        <div className="border-t border-border pt-4">
                            <Row
                                strong
                                label={tr(
                                    "\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a",
                                    "Total"
                                )}
                                value={`${money(
                                    grandTotal
                                )} ${tr(
                                    "\u0631.\u0633",
                                    "SAR"
                                )}`}
                            />
                        </div>
                    </div>
                </section>


                {!official
                    && shippingTotal > 0
                    && totals.shipping_allocation_status
                        !== "allocated"
                    && (
                        <div className="mt-6 text-xs text-muted-foreground">
                            {tr(
                                "\u0645\u0639\u0627\u0644\u062c\u0629 \u0631\u0633\u0648\u0645 \u0627\u0644\u0634\u062d\u0646 \u0636\u0645\u0646 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629 \u0642\u064a\u062f \u0627\u0644\u062a\u062c\u0647\u064a\u0632.",
                                "Note: Shipping allocation for this invoice is still being prepared."
                            )}
                        </div>
                    )}
            </div>
        </Layout>
    );
}


function Row({
    label,
    value,
    strong = false,
}) {
    return (
        <div className="flex items-start justify-between gap-4">
            <span className="text-muted-foreground">
                {label}
            </span>

            <span
                className={
                    strong
                        ? "font-black text-espresso text-base"
                        : "font-bold text-espresso text-end"
                }
            >
                {value ?? "-"}
            </span>
        </div>
    );
}
